import { continueButton } from './ConfirmDialog.locstring.json';
import { cancelButton } from 'owa-locstrings/lib/strings/cancelbutton.locstring.json';
import { okButton } from 'owa-locstrings/lib/strings/okbutton.locstring.json';
import loc from 'owa-localize';
import { observer } from 'mobx-react';
import { ConfirmCustomizationOptions } from '../actions/confirm';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { Dialog, DialogFooter, DialogType } from 'office-ui-fabric-react/lib/Dialog';

import * as React from 'react';
import * as ReactDOM from 'react-dom';

let styles = require('./ConfirmDialog.scss');

interface ConfirmDialogProps {
    title?: string;
    subText?: string;
    customizationOptions?: ConfirmCustomizationOptions;
    onOkCallback?: () => void;
    onCancelCallback?: () => void;
    onDismissCallback?: () => void;
    onContinueCallback?: () => void;
    onClose: () => void;
}

interface ConfirmDialogState {
    isShown: boolean;
}

let counter = 0;

@observer
class ConfirmDialog extends React.Component<ConfirmDialogProps, ConfirmDialogState> {
    public static defaultProps = {
        customizationOptions: {},
    };

    private delayedCallback: () => void;
    private cancelId = `cancel-${counter++}`;
    private okId = `ok-${counter++}`;
    private continueId = `continue-${counter++}`;

    constructor(props: any) {
        super(props);
        this.state = {
            isShown: true,
        };
    }

    private onOk = () => this.onClick(this.props.onOkCallback);
    private onCancel = () => this.onClick(this.props.onCancelCallback);
    private onContinue = () => this.onClick(this.props.onContinueCallback);
    private onSubmit = evt => {
        evt.preventDefault();
        this.onOk();
    };

    render() {
        const customizationOptions = this.props.customizationOptions;

        return (
            <Dialog
                isOpen={this.state.isShown}
                type={DialogType.normal}
                onDismiss={this.onDismiss}
                onDismissed={this.onDismissed}
                subText={this.props.subText}
                title={this.props.title}
                containerClassName={customizationOptions.containerClassName}
                contentClassName={styles.content}
                modalProps={{
                    firstFocusableSelector: this.firstFocusableSelector,
                }}>
                {/** We have the form so that 'enter' in input fields will trigger the 'ok' confirm action */}

                <form onSubmit={this.onSubmit}>
                    {customizationOptions.bodyElement}
                    <input className={styles.hideSubmitButton} type="submit" />
                </form>

                <DialogFooter>
                    <PrimaryButton
                        id={this.okId}
                        onClick={this.onOk}
                        title={customizationOptions.okToolTip}>
                        {customizationOptions.okText || loc(okButton)}
                    </PrimaryButton>
                    {customizationOptions.showContinueButton ? (
                        <DefaultButton
                            id={this.continueId}
                            onClick={this.onContinue}
                            title={customizationOptions.continueToolTip}>
                            {customizationOptions.continueText || loc(continueButton)}
                        </DefaultButton>
                    ) : null}
                    {customizationOptions.hideCancelButton ? null : (
                        <DefaultButton
                            id={this.cancelId}
                            onClick={this.onCancel}
                            title={customizationOptions.cancelToolTip}>
                            {customizationOptions.cancelText || loc(cancelButton)}
                        </DefaultButton>
                    )}
                </DialogFooter>
            </Dialog>
        );
    }

    private get firstFocusableSelector(): string {
        const { hideCancelButton, focusOnCancel, focusSelector } = this.props.customizationOptions;
        let selector: string;
        if (!hideCancelButton && focusOnCancel) {
            selector = `#${this.cancelId}`;
        } else if (focusSelector) {
            selector = focusSelector;
        } else {
            selector = `#${this.okId}`;
        }

        // The FocusTrapZone, inexplicably, prepends any `firstFocusableSelector` provided with a '.',
        // so trying to provide a straight id selector ends up throwing an exception.
        // To work around the issue, I'm prepending the selector with a class applied
        // to the content of the Dialog to "eat" the prepended '.'
        return `ms-Dialog-inner ${selector}`;
    }

    private onClick = callback => {
        if (callback) {
            if (
                this.props.customizationOptions &&
                this.props.customizationOptions.delayCallbackAfterAnimation
            ) {
                this.delayedCallback = callback;
            } else {
                callback();
            }
        }

        this.setState({
            isShown: false,
        });
    };

    private onDismiss = () => {
        if (this.props.onDismissCallback) {
            // We dismissed without issuing a callback.
            // Call 'cancel'
            this.props.onDismissCallback();
        }

        if (!this.props.customizationOptions.preventSoftDismiss) {
            this.setState({
                isShown: false,
            });
        }
    };

    private onDismissed = () => {
        if (this.delayedCallback) {
            this.delayedCallback();
            this.delayedCallback = null;
        }

        this.props.onClose();
    };
}

let instance = 0;

function showConfirmDialog(
    title: string,
    subText?: string,
    customizationOptions?: ConfirmCustomizationOptions,
    onOkCallback?: () => void,
    onCancelCallback?: () => void,
    onDismissCallback?: () => void,
    onContinueCallback?: () => void
) {
    // Initialize it and append it to the body.
    let confirmDialogDiv = document.createElement('div');
    confirmDialogDiv.id = `confirmDialog${instance++}`;
    document.body.appendChild(confirmDialogDiv);

    function cleanup() {
        ReactDOM.unmountComponentAtNode(confirmDialogDiv);
        document.body.removeChild(confirmDialogDiv);
    }

    ReactDOM.render(
        <ConfirmDialog
            title={title}
            subText={subText}
            customizationOptions={customizationOptions}
            onOkCallback={onOkCallback}
            onCancelCallback={onCancelCallback}
            onDismissCallback={onDismissCallback}
            onContinueCallback={onContinueCallback}
            onClose={cleanup}
        />,
        confirmDialogDiv
    );
}

export default showConfirmDialog;
